import { Component, OnInit, OnDestroy, HostListener, ViewChild } from '@angular/core';
import { ControlExamenService, ExamSession } from '../../services/control-examen.service';
import { EstudianteExamen } from './models/estudiante-examen.model';
import { PreciosExamen } from './models/precios-config.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RegistroRecepcionComponent } from './registro-recepcion/registro-recepcion.component';


@Component({
  selector: 'app-control-examen',
  templateUrl: './control-examen.component.html',
  styleUrls: ['./control-examen.component.css']
})
export class ControlExamenComponent implements OnInit, OnDestroy {
  sesiones$: Observable<ExamSession[]>;
  currentSessionId: string | null = null;

  isExamenActivo$: Observable<boolean>;
  estudiantesExamen$: Observable<EstudianteExamen[]>;
  showDropdown = false;
  private isExamenActivo = false;

  preciosExamen$: Observable<PreciosExamen>;
  showPreciosModal = false;
  preciosMaderasSectionExpanded: boolean = false;
  preciosForm: PreciosExamen = new PreciosExamen();

  @ViewChild(RegistroRecepcionComponent) registroRecepcion!: RegistroRecepcionComponent;

  constructor(private controlExamenService: ControlExamenService) {
    this.isExamenActivo$ = this.controlExamenService.isExamenActivo$;
    this.estudiantesExamen$ = this.controlExamenService.estudiantesExamen$;
    this.preciosExamen$ = this.controlExamenService.preciosExamen$;
    this.sesiones$ = this.controlExamenService.sesiones$;
  }

  ngOnInit(): void {
    this.controlExamenService.sesiones$.subscribe(sesiones => {
      // Keep current session ID in sync
    });
    
    this.controlExamenService.currentSessionId$.subscribe(id => {
      this.currentSessionId = id;
    });

    // Subscribe to exam status to track it locally
    this.isExamenActivo$.subscribe(activo => {
      this.isExamenActivo = activo;
    });
    // Subscribe to precios to keep local form updated
    this.preciosExamen$.subscribe(precios => {
      this.preciosForm = new PreciosExamen(precios);
    });
  }

  ngOnDestroy(): void {
    // Clean up when component is destroyed
    this.controlExamenService.finalizarExamen();
  }

  // Warn before closing window/tab when exam is active
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.isExamenActivo) {
      // Modern browsers ignore custom messages, but still show a generic warning
      event.preventDefault();
      event.returnValue = 'Hay un examen activo. ¿Está seguro de que desea salir?';
      return;
    }
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.mobile-dropdown')) {
      this.showDropdown = false;
    }
  }

  // Prevent accidental F5 refresh during exam
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.isExamenActivo) {
      // Prevent F5 refresh
      if (event.key === 'F5' || (event.ctrlKey && event.key === 'r')) {
        event.preventDefault();
        if (confirm('¿Está seguro de que desea recargar la página? El examen está activo.')) {
          window.location.reload();
        }
        return;
      }
      
      // Prevent Ctrl+W (close tab)
      if (event.ctrlKey && event.key === 'w') {
        event.preventDefault();
        if (confirm('¿Está seguro de que desea cerrar esta pestaña? El examen está activo.')) {
          window.close();
        }
        return;
      }
      
      // Prevent Ctrl+Shift+T (reopen closed tab) - not really preventable but we can warn
      if (event.ctrlKey && event.shiftKey && event.key === 'T') {
        console.warn('Usuario intentó reabrir pestaña durante examen activo');
      }
    }
  }

  iniciarExamen(): void {
    const hora = new Date();
    const horaStr = `${hora.getHours().toString().padStart(2, '0')}:${hora.getMinutes().toString().padStart(2, '0')}`;
    
    const nombre = prompt('Nombre del examen:', `Examen ${horaStr}h`);
    if (nombre) {
      this.controlExamenService.iniciarExamen(nombre);
      // Show prices configuration modal
      this.showPreciosModal = true;
    }
  }

  guardarPrecios(): void {
    this.controlExamenService.setPreciosExamen(this.preciosForm);
    this.showPreciosModal = false;
  }

  cancelarPrecios(): void {
    // Just close modal without saving
    this.showPreciosModal = false;
  }

  finalizarExamen(): void {
    if (confirm('¿Está seguro de que desea finalizar el control de examen?')) {
      this.controlExamenService.finalizarExamen();
    }
  }

  onAddNewSession(): void {
    const hora = new Date();
    const horaStr = `${hora.getHours().toString().padStart(2, '0')}:${hora.getMinutes().toString().padStart(2, '0')}`;
    
    const nombre = prompt('Nombre del examen:', `Examen ${horaStr}h`);
    if (nombre) {
      this.controlExamenService.crearNuevaSession(nombre, hora);
    }
  }

  onChangeSession(): void {
    this.controlExamenService.cambiarSession(this.currentSessionId);
    this.registroRecepcion?.limpiarFormulario(); // Clear form when switching
  }

  onEstudianteAgregado(estudiante: EstudianteExamen): void {
    this.controlExamenService.agregarEstudianteRecepcion(estudiante);
  }

  onEstudianteEliminado(nroLibreta: number): void {
    if (confirm('¿Está seguro de que desea eliminar este estudiante?')) {
      this.controlExamenService.eliminarEstudiante(nroLibreta);
    }
  }

  onEstudianteEditado(estudiante: EstudianteExamen): void {
    if (this.registroRecepcion) {
      this.registroRecepcion.cargarDatosEstudiante(estudiante);
      // Scroll to the form
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  actualizarTodos(): void {
    this.controlExamenService.actualizarTodos();
  }

  exportarExcel(): void {
    const csvData = this.controlExamenService.tablaCSV();
    this.downloadCSV(csvData, 'inscriptos-examen-completo');
  }

  exportarExcelSession(): void {
    const csvData = this.controlExamenService.tablaCSVSession();
    const session = this.controlExamenService.getCurrentSession();
    const sessionName = session?.nombre || 'sesion';
    this.downloadCSV(csvData, `inscriptos-${sessionName.replace(/\s+/g, '_')}`);
  }

  private downloadCSV(csvData: string, filename: string): void {
    const BOM = "\uFEFF";
    const csvWithBOM = BOM + csvData;
    
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const now = new Date();
    const argentinaTime = new Intl.DateTimeFormat('es-AR', {
      timeZone: 'America/Argentina/Buenos_Aires',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(now);
    
    const parts = argentinaTime.split(', ');
    const dateParts = parts[0].split('/');
    const timeParts = parts[1].replace(':', '-');
    const timestamp = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}_T${timeParts}`;

    const link = document.createElement('a');
    link.download = `${filename}-${timestamp}.csv`;
    link.href = url;
    link.click();
  }

  exportarDatos(): void {
    const data = this.controlExamenService.exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `control-examen-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (this.controlExamenService.importData(content)) {
          alert('Datos importados correctamente');
        } else {
          alert('Error al importar los datos');
        }
      };
      reader.readAsText(file);
    }
  }

  togglePreciosMaderasSection(): void {
    this.preciosMaderasSectionExpanded = !this.preciosMaderasSectionExpanded;
  }

  // Dropdown methods
  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  closeDropdown(): void {
    this.showDropdown = false;
  }
}